import React, { useEffect } from "react";
import {
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { get_root_value } from "src/utils/domUtils";
import { paceFromVdot, getKmhr, getMihr } from "src/utils/constant";

const PaceCalculator = ({
  checkedPace,
  memberVdotData,
  setMemberVdotDataData,
  member_vdot,
  member_vdot_info,
}) => {
  const findPaceObjectByVdot = (vdotValue) => {
    return paceFromVdot.find((paceObject) => paceObject.vdot == vdotValue);
  };

  const vdotObject = findPaceObjectByVdot(member_vdot);

  useEffect(() => {
    if (!checkedPace || !setMemberVdotDataData) return;

    setMemberVdotDataData((prevState) => ({
      ...prevState,
      distance: member_vdot_info?.race_type || prevState.distance || "",
      time_interval:
        parseInt(member_vdot_info?.time_taken) || prevState.time_interval || 0,
      pace: vdotObject?.[prevState.easy_long + "_low"] || prevState.pace || 0,
      paceUp:
        vdotObject?.[prevState.easy_long + "_up"] || prevState.paceUp || 0,
      pace1: vdotObject?.[prevState.thre_shold] || prevState.pace1 || 0,
      pace2: vdotObject?.[prevState.interval] || prevState.pace2 || 0,
    }));
  }, [checkedPace, member_vdot_info, member_vdot, setMemberVdotDataData]);

  const handleChangeOther = (event) => {
    const { name, value } = event.target;

    setMemberVdotDataData((prevState) => {
      const newState = {
        ...prevState,
        [name]: value,
      };

      if (name === "easy_long") {
        newState.pace = vdotObject?.[value + "_low"] || 0;
        newState.paceUp = vdotObject?.[value + "_up"] || 0;
      } else if (name === "thre_shold") {
        newState.pace1 = vdotObject?.[value] || 0;
      } else if (name === "interval") {
        newState.pace2 = vdotObject?.[value] || 0;
      }

      return newState;
    });
  };

  const getPaceText = (type) => {
    if (type?.includes("mi")) return "Pace Min/Mile";
    if (type?.includes("km")) return "Pace Min/Km";
    return "Time Cap";
  };

  const handleChangeVdotDistance = (name) => {
    switch (name) {
      case "i":
        return [
          { title: "400 Meters Pace", key: "i_400_m" },
          { title: "1 KM Pace", key: "i_km" },
          { title: "1 MILE Pace", key: "i_mi" },
        ];
      case "t":
        return [
          { title: "1 KM Pace", key: "t_km" },
          { title: "1 MILE Pace", key: "t_mi" },
        ];
      default:
        return [
          { title: "1 KM Pace", key: "e_km" },
          { title: "1 MILE Pace", key: "e_mi" },
        ];
    }
  };

  const formatWithLeadingZeros = (value) => {
    const n = parseInt(value) || 0;
    return n < 10 ? `0${n}` : `${n}`;
  };

  const distanceLabel = {
    "5km": "5 (km)",
    "10km": "10 (km)",
    "15km": "15 (km)",
    half_marathon: "Half Marathon",
    marathon: "Marathon",
  };

  const is_allow =
    memberVdotData?.pace ||
    memberVdotData?.paceUp ||
    memberVdotData?.pace1 ||
    memberVdotData?.pace2;

  if (!checkedPace) return null;

  return (
    <div className="row">
      <div className="col-12 col-md-6 mt-3">
        <FormControl size="small" fullWidth variant="outlined" disabled>
          <InputLabel
            id="pace-distance-label"
            sx={{ color: get_root_value("--input-text-color") }}
          >
            Distance
          </InputLabel>
          <Select
            className="svg-color"
            labelId="pace-distance-label"
            value={memberVdotData.distance || ""}
            name="distance"
            label="Distance"
            size="small"
            sx={{ color: get_root_value("--input-text-color") }}
          >
            {Object.entries(distanceLabel).map(([val, label]) => (
              <MenuItem key={val} value={val}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      <div className="col-12 col-md-6 mt-3">
        <TextField
          variant="outlined"
          label="Duration (hh:mm:ss)"
          size="small"
          disabled
          InputLabelProps={{ shrink: true }}
          InputProps={{
            startAdornment: (
              <InputAdornment
                position="start"
                style={{ display: "flex", alignItems: "center" }}
              >
                <TextField
                  type="number"
                  size="small"
                  placeholder="Hrs"
                  disabled
                  value={formatWithLeadingZeros(
                    Math.floor(memberVdotData.time_interval / 3600),
                  )}
                  sx={{
                    "& fieldset": { border: "none" },
                    "& input": { textAlign: "center", padding: "0px" },
                    width: "20%",
                  }}
                />
                <div className="fs-5 fw-semibold">:</div>
                <TextField
                  type="number"
                  size="small"
                  placeholder="Min"
                  disabled
                  value={formatWithLeadingZeros(
                    Math.floor((memberVdotData.time_interval % 3600) / 60),
                  )}
                  sx={{
                    "& fieldset": { border: "none" },
                    "& input": { textAlign: "center", padding: "0px" },
                    width: "20%",
                  }}
                />
                <div className="fs-5 fw-semibold">:</div>
                <TextField
                  type="number"
                  size="small"
                  placeholder="Sec"
                  disabled
                  value={formatWithLeadingZeros(
                    memberVdotData.time_interval % 60,
                  )}
                  sx={{
                    "& fieldset": { border: "none" },
                    "& input": { textAlign: "center", padding: "0px" },
                    width: "20%",
                  }}
                />
              </InputAdornment>
            ),
          }}
          sx={{ borderRadius: "5px", fontSize: "13px", width: "100%" }}
        />
      </div>

      {is_allow && (
        <>
          <div className="col-12 mt-2">
            <p className="mb-0">Your Calculated Pace:</p>
          </div>

          {/* ── Easy / Long ── */}
          <div className="col-12 col-md-6 mt-3">
            <FormControl size="small" fullWidth variant="outlined">
              <InputLabel
                id="pace-easy-long-label"
                sx={{ color: get_root_value("--input-text-color") }}
              >
                Easy/Long
              </InputLabel>
              <Select
                className="svg-color"
                labelId="pace-easy-long-label"
                value={memberVdotData.easy_long || ""}
                name="easy_long"
                label="Easy/Long"
                onChange={handleChangeOther}
                size="small"
                sx={{ color: get_root_value("--input-text-color") }}
              >
                {handleChangeVdotDistance().map((option) => (
                  <MenuItem key={option.key} value={option.key}>
                    {option.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div className="col-12 col-md-6 mt-3">
            <TextField
              variant="outlined"
              label={getPaceText(memberVdotData?.easy_long)}
              InputLabelProps={{
                className: "textfiels-input-label",
                shrink: true,
                style: { fontSize: "14px" },
              }}
              InputProps={{
                className: "textfiels-input-value",
                style: {
                  fontSize: "13px",
                  paddingLeft: "10px",
                  paddingRight: "10px",
                },
                readOnly: true,
                endAdornment: (
                  <InputAdornment
                    position="end"
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <TextField
                      type="number"
                      size="small"
                      placeholder="Min"
                      disabled
                      value={formatWithLeadingZeros(
                        Math.floor(memberVdotData?.pace / 60),
                      )}
                      InputProps={{
                        className: "textfiels-input-value",
                        style: { fontSize: "14px" },
                        readOnly: true,
                      }}
                      sx={{
                        "& fieldset": { border: "none" },
                        "& input": { textAlign: "center", padding: "0px" },
                        width: "100%",
                      }}
                    />
                    <div className="fs-6 fw-semibold ms-1">:</div>
                    <TextField
                      type="number"
                      size="small"
                      placeholder="Sec"
                      disabled
                      value={formatWithLeadingZeros(memberVdotData?.pace % 60)}
                      InputProps={{
                        className: "textfiels-input-value",
                        style: { fontSize: "14px" },
                        readOnly: true,
                      }}
                      sx={{
                        "& fieldset": { border: "none" },
                        "& input": { textAlign: "center", padding: "0px" },
                        width: "100%",
                      }}
                    />
                    <div className="fs-6 fw-semibold ms-1 me-1">-</div>
                    <TextField
                      type="number"
                      size="small"
                      placeholder="Min"
                      disabled
                      value={formatWithLeadingZeros(
                        Math.floor(memberVdotData?.paceUp / 60),
                      )}
                      InputProps={{
                        className: "textfiels-input-value",
                        style: { fontSize: "14px" },
                        readOnly: true,
                      }}
                      sx={{
                        "& fieldset": { border: "none" },
                        "& input": { textAlign: "center", padding: "0px" },
                        width: "100%",
                      }}
                    />
                    <div className="fs-6 fw-semibold ms-1">:</div>
                    <TextField
                      type="number"
                      size="small"
                      disabled
                      placeholder="Sec"
                      value={formatWithLeadingZeros(
                        memberVdotData?.paceUp % 60,
                      )}
                      InputProps={{
                        className: "textfiels-input-value",
                        style: { fontSize: "14px" },
                        readOnly: true,
                      }}
                      sx={{
                        "& fieldset": { border: "none" },
                        "& input": { textAlign: "center", padding: "0px" },
                        width: "100%",
                        marginRight: 1,
                      }}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                borderRadius: "5px",
                fontSize: "13px",
                width: "100%",
                "& .css-e9crry-MuiInputBase-input-MuiOutlinedInput-input": {
                  width: "0px !important",
                  padding: "9px 0px !important",
                },
                "& .css-1nuss9t": {
                  width: "0px !important",
                  padding: "9px 0px !important",
                },
              }}
            />
            {memberVdotData?.pace > 0 && (
              <Typography
                variant="subtitle2"
                sx={{
                  opacity: 0.4,
                  fontWeight: "600",
                  fontSize: "12px",
                  mt: 0.5,
                }}
              >{` @ Speed ${getKmhr(
                memberVdotData?.pace,
                memberVdotData?.easy_long,
              )} km/hr or ${getMihr(
                memberVdotData?.pace,
                memberVdotData?.easy_long,
              )} mi/hr - ${getKmhr(
                memberVdotData?.paceUp,
                memberVdotData?.easy_long,
              )} km/hr or ${getMihr(
                memberVdotData?.paceUp,
                memberVdotData?.easy_long,
              )} mi/hr`}</Typography>
            )}
          </div>

          {/* ── Threshold ── */}
          <div className="col-12 col-md-6 mt-3">
            <FormControl size="small" fullWidth variant="outlined">
              <InputLabel
                id="pace-threshold-label"
                sx={{ color: get_root_value("--input-text-color") }}
              >
                Threshold
              </InputLabel>
              <Select
                className="svg-color"
                labelId="pace-threshold-label"
                value={memberVdotData.thre_shold || ""}
                name="thre_shold"
                label="Threshold"
                onChange={handleChangeOther}
                size="small"
                sx={{ color: get_root_value("--input-text-color") }}
              >
                {handleChangeVdotDistance("t").map((option) => (
                  <MenuItem key={option.key} value={option.key}>
                    {option.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div className="col-12 col-md-6 mt-3">
            <TextField
              variant="outlined"
              label={getPaceText(memberVdotData?.thre_shold)}
              InputLabelProps={{
                className: "textfiels-input-label",
                shrink: true,
                style: { fontSize: "14px" },
              }}
              InputProps={{
                className: "textfiels-input-value",
                style: {
                  fontSize: "13px",
                  paddingLeft: "10px",
                  paddingRight: "10px",
                },
                readOnly: true,
                endAdornment: (
                  <InputAdornment
                    position="end"
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <TextField
                      type="number"
                      size="small"
                      placeholder="Min"
                      disabled
                      value={formatWithLeadingZeros(
                        Math.floor(memberVdotData?.pace1 / 60),
                      )}
                      InputProps={{
                        className: "textfiels-input-value",
                        style: { fontSize: "14px" },
                        readOnly: true,
                      }}
                      sx={{
                        "& fieldset": { border: "none" },
                        "& input": { textAlign: "center", padding: "0px" },
                        width: "100%",
                      }}
                    />
                    <div className="fs-6 fw-semibold ms-1">:</div>
                    <TextField
                      type="number"
                      size="small"
                      placeholder="Sec"
                      disabled
                      value={formatWithLeadingZeros(memberVdotData?.pace1 % 60)}
                      InputProps={{
                        className: "textfiels-input-value",
                        style: { fontSize: "14px" },
                        readOnly: true,
                      }}
                      sx={{
                        "& fieldset": { border: "none" },
                        "& input": { textAlign: "center", padding: "0px" },
                        width: "100%",
                      }}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                borderRadius: "5px",
                fontSize: "13px",
                width: "100%",
                "& .css-e9crry-MuiInputBase-input-MuiOutlinedInput-input": {
                  width: "0px !important",
                  padding: "9px 0px !important",
                },
                "& .css-1nuss9t": {
                  width: "0px !important",
                  padding: "9px 0px !important",
                },
              }}
            />
            {memberVdotData?.pace1 > 0 && (
              <Typography
                variant="subtitle2"
                sx={{
                  opacity: 0.4,
                  fontWeight: "600",
                  fontSize: "12px",
                  mt: 0.5,
                }}
              >{` @ Speed ${getKmhr(
                memberVdotData?.pace1,
                memberVdotData?.thre_shold,
              )} km/hr or ${getMihr(
                memberVdotData?.pace1,
                memberVdotData?.thre_shold,
              )} mi/hr`}</Typography>
            )}
          </div>

          {/* ── Interval ── */}
          <div className="col-12 col-md-6 mt-3">
            <FormControl size="small" fullWidth variant="outlined">
              <InputLabel
                id="pace-interval-label"
                sx={{ color: get_root_value("--input-text-color") }}
              >
                Interval
              </InputLabel>
              <Select
                className="svg-color"
                labelId="pace-interval-label"
                value={memberVdotData.interval || ""}
                name="interval"
                label="Interval"
                onChange={handleChangeOther}
                size="small"
                sx={{ color: get_root_value("--input-text-color") }}
              >
                {handleChangeVdotDistance("i").map((option) => (
                  <MenuItem key={option.key} value={option.key}>
                    {option.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div className="col-12 col-md-6 mt-3">
            <TextField
              variant="outlined"
              label={getPaceText(memberVdotData?.interval)}
              InputLabelProps={{
                className: "textfiels-input-label",
                shrink: true,
                style: { fontSize: "14px" },
              }}
              InputProps={{
                className: "textfiels-input-value",
                style: {
                  fontSize: "13px",
                  paddingLeft: "10px",
                  paddingRight: "10px",
                },
                readOnly: true,
                endAdornment: (
                  <InputAdornment
                    position="end"
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <TextField
                      type="number"
                      size="small"
                      placeholder="Min"
                      disabled
                      value={formatWithLeadingZeros(
                        Math.floor(memberVdotData?.pace2 / 60),
                      )}
                      InputProps={{
                        className: "textfiels-input-value",
                        style: { fontSize: "14px" },
                        readOnly: true,
                      }}
                      sx={{
                        "& fieldset": { border: "none" },
                        "& input": { textAlign: "center", padding: "0px" },
                        width: "100%",
                      }}
                    />
                    <div className="fs-6 fw-semibold ms-1">:</div>
                    <TextField
                      type="number"
                      size="small"
                      placeholder="Sec"
                      disabled
                      value={formatWithLeadingZeros(memberVdotData?.pace2 % 60)}
                      InputProps={{
                        className: "textfiels-input-value",
                        style: { fontSize: "14px" },
                        readOnly: true,
                      }}
                      sx={{
                        "& fieldset": { border: "none" },
                        "& input": { textAlign: "center", padding: "0px" },
                        width: "100%",
                      }}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                borderRadius: "5px",
                fontSize: "13px",
                width: "100%",
                "& .css-e9crry-MuiInputBase-input-MuiOutlinedInput-input": {
                  width: "0px !important",
                  padding: "9px 0px !important",
                },
                "& .css-1nuss9t": {
                  width: "0px !important",
                  padding: "9px 0px !important",
                },
              }}
            />
            {memberVdotData?.pace2 > 0 && (
              <Typography
                variant="subtitle2"
                sx={{
                  opacity: 0.4,
                  fontWeight: "600",
                  fontSize: "12px",
                  mt: 0.5,
                }}
              >{` @ Speed ${getKmhr(
                memberVdotData?.pace2,
                memberVdotData?.interval,
              )} km/hr or ${getMihr(
                memberVdotData?.pace2,
                memberVdotData?.interval,
              )} mi/hr`}</Typography>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default PaceCalculator;
